import { useState, useEffect } from "react";
import { Check, X, AlertTriangle, Shield, Tag as TagIcon } from "lucide-react";
import { userStore } from "../../stores/user.store";
import { observer } from "mobx-react-lite";
import { Select } from "../design/Select";
import type { User } from "../types";
import { cn } from "@/utils/cn";
import { tagColor } from "@/utils/tag-colors";
import { PERMISSIONS, PERMISSION_GROUPS, can, presetPermissions } from '@/utils/permissions';

const UserModal = ({
  isOpen,
  onClose,
  onSave,
  userToEdit,
}: {
  isOpen: boolean;
  onClose: () => void;
  onSave: (u: User) => void;
  userToEdit: User | null;
}) => {
  const defaultUser: User = {
    id: 0,
    username: "",
    role: "User",
    avatar_color: "blue",
    // Default = "User"-Preset: nur zusehen, nichts kaputt machen koennen.
    permissions: presetPermissions("User"),
    tags: []
  };
  const [formData, setFormData] = useState<User>(defaultUser);

  useEffect(() => {
    userStore.fetchTags();
  }, []);

  useEffect(() => {
    if (userToEdit) {
      setFormData({
        ...defaultUser,
        ...userToEdit,
        permissions: {
          ...defaultUser.permissions,
          ...(userToEdit.permissions || {}),
        },
      });
    } else {
      setFormData(defaultUser);
    }
  }, [userToEdit, isOpen]);

  // Logische Presets:
  //  - User      = Zuschauer: Konsole + Statistiken lesen, sonst nichts.
  //  - Moderator = Spieler managen: + Power, Moderation, Zeitplaene.
  //                KEINE Dateien/Backups (zerstoerungsfrei), kein System.
  //  - Owner     = alles.
  const handleRoleChange = (role: string) => {
    setFormData({ ...formData, role, permissions: { ...formData.permissions, ...presetPermissions(role) } });
  };

  const togglePerm = (key: string) => {
    if (!can(userStore.currentUser, key)) return; // nur Rechte, die man selbst hat (Backend prüft das auch)
    const newPerms = {
      ...formData.permissions,
      [key]: !formData.permissions[key],
    };

    // Sync sub-permissions with master switch
    // (Consolidated simplified logic requires less dependency checks)

    setFormData({
      ...formData,
      permissions: newPerms,
    });
  };

  const toggleTag = (tagId: string) => {
      const currentTags = formData.tags || [];
      if (currentTags.includes(tagId)) {
          setFormData({ ...formData, tags: currentTags.filter(id => id !== tagId) });
      } else {
          setFormData({ ...formData, tags: [...currentTags, tagId] });
      }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
      <div className="bg-surface border border-line rounded-2xl w-full max-w-2xl max-h-[90vh] flex flex-col overflow-hidden shadow-2xl">
        <div className="p-4 md:p-6 border-b border-line flex justify-between items-center bg-ink/50 flex-shrink-0">
          <h3 className="text-lg md:text-xl font-bold text-white">
            {userToEdit ? "Nutzer bearbeiten" : "Neuen Nutzer anlegen"}
          </h3>
          <button
            onClick={onClose}
            className="p-2 text-fg-muted hover:text-white hover:bg-surface-2 rounded-lg transition-all"
          >
            <X size={24} />
          </button>
        </div>
        <div className="p-4 md:p-8 space-y-6 overflow-y-auto flex-1">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 md:gap-6">
            <div>
              <label className="block text-xs uppercase text-fg-muted font-bold mb-2">
                Benutzername
              </label>
              <input
                type="text"
                value={formData.username}
                onChange={(e) =>
                  setFormData({ ...formData, username: e.target.value })
                }
                className="w-full bg-gray-950 border border-line rounded-lg p-3 text-white focus:border-blue-500 outline-none text-sm md:text-base"
              />
            </div>
            <div>
              <label className="block text-xs uppercase text-fg-muted font-bold mb-2">
                Rolle (Preset)
              </label>
              <Select
                value={formData.role}
                onChange={handleRoleChange}
                options={[
                  { value: "User", label: "User" },
                  { value: "Moderator", label: "Moderator" },
                  ...(userStore.currentUser?.role === "Owner" ? [{ value: "Owner", label: "Owner (alle Rechte)" }] : []),
                ]}
              />
            </div>
          </div>
          <div className="space-y-4">
            <h4 className="text-xs uppercase text-fg-muted font-bold mb-2">Detaillierte Berechtigungen</h4>
            
            {PERMISSION_GROUPS.map((g) => ({
                title: g.label,
                hint: g.hint,
                perms: PERMISSIONS.filter((p) => p.group === g.id).map((p) => ({
                    k: p.key, l: p.label, d: p.description, w: p.danger === "warn", r: p.danger === "critical",
                })),
            })).map((group) => (
                <div key={group.title} className="bg-ink/30 border border-line rounded-xl overflow-hidden">
                    <div className="bg-ink/50 px-3 py-2 border-b border-line">
                        <span className="text-xs font-bold text-fg-muted uppercase tracking-widest">{group.title}</span>
                        <span className="block text-[11px] text-fg-muted/80 mt-0.5">{group.hint}</span>
                    </div>
                    <div className="p-2 grid grid-cols-1 gap-2">
                        {group.perms.map(({ k, l, d, w, r }) => (
                            <div
                                key={k}
                                onClick={() => togglePerm(k)}
                                title={can(userStore.currentUser, k) ? undefined : "Dieses Recht hast du selbst nicht – nur jemand, der es hat, kann es vergeben."}
                                className={`flex items-center p-2 rounded-lg transition-all ${can(userStore.currentUser, k) ? "cursor-pointer" : "opacity-50 cursor-not-allowed"} ${
                                    formData.permissions[k]
                                    ? "bg-blue-900/20 border border-blue-500/30"
                                    : "hover:bg-surface-2 border border-transparent"
                                }`}
                            >
                                <div
                                    className={`w-4 h-4 rounded border flex items-center justify-center mr-3 flex-shrink-0 transition-colors ${
                                    formData.permissions[k]
                                        ? "bg-blue-500 border-blue-500"
                                        : "border-line bg-ink"
                                    }`}
                                >
                                    {formData.permissions[k] && (
                                        <Check size={12} className="text-white" />
                                    )}
                                </div>
                                <span className="min-w-0">
                                    <span className={`block text-sm ${r ? "text-red-400 font-medium" : "text-fg"}`}>{l}</span>
                                    <span className="block text-xs text-fg-muted mt-0.5 leading-snug">{d}</span>
                                </span>
                                {w && <AlertTriangle size={14} className="ml-auto text-yellow-500/50" />}
                                {r && <Shield size={14} className="ml-auto text-red-500/50" />}
                            </div>
                        ))}
                    </div>
                </div>
            ))}
          </div>

          <div className="space-y-4 pt-4 border-t border-line">
             <h4 className="text-xs uppercase text-fg-muted font-bold mb-2 flex items-center">
                 <TagIcon size={14} className="mr-2" /> Tags – welche Server sieht dieser Nutzer?
             </h4>
             <p className="text-xs text-fg-muted -mt-2">Ohne Tag sieht der Nutzer alle Server, mit Tag nur die Server seiner Tags. Owner sehen immer alles.</p>
             <div className="flex flex-wrap gap-2">
                 {/* Only allow tag editing if current user is Global Admin (no tags) */}
                 {(!userStore.currentUser?.tags || userStore.currentUser.tags.length === 0) ? (
                     userStore.tags.map(tag => {
                         const isSelected = formData.tags?.includes(tag.id);
                         return (
                             <button
                                 key={tag.id}
                                 onClick={() => toggleTag(tag.id)}
                                 className={cn("flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-medium transition-all border", isSelected ? cn(tagColor(tag.color).soft, "text-fg") : "bg-ink text-fg-muted border-line hover:bg-surface-2")}
                             >
                                 <span className={cn("w-2 h-2 rounded-full", tagColor(tag.color).dot)} />
                                 <span>{tag.name}</span>
                                 {isSelected && <Check size={12} />}
                             </button>
                         );
                     })
                 ) : (
                     <p className="text-fg-muted text-sm">
                        Tags werden für Sub-User automatisch basierend auf deinen Berechtigungen vererbt.
                     </p>
                 )}
                 {userStore.tags.length === 0 && (
                     <p className="text-fg-muted text-sm italic">Keine Tags verfügbar. Erstelle welche in der Übersicht.</p>
                 )}
             </div>
          </div>
        </div>
        <div className="p-4 md:p-6 bg-ink/50 border-t border-line flex justify-end space-x-3 flex-shrink-0">
          <button
            onClick={onClose}
            className="px-4 md:px-6 py-2 rounded-lg text-fg-muted hover:text-white hover:bg-surface-2 transition-all text-sm md:text-base"
          >
            Abbrechen
          </button>
          <button
            onClick={() => onSave(formData)}
            className="px-4 md:px-6 py-2 rounded-lg bg-blue-600 hover:bg-blue-500 text-white font-bold shadow-lg shadow-blue-900/20 transition-all text-sm md:text-base"
          >
            Speichern
          </button>
        </div>
      </div>
    </div>
  );
};

export default observer(UserModal);

